import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { QuickView } from "../components/QuickView";
import { Reveal, MaskedLine } from "../components/Reveal";
import { DropCountdown } from "../components/DropCountdown";

const FILTERS = [
  { id: "all", name: "All", tagline: "Every pair in the maison." },
  { id: "classic-vintage", name: "Classic Vintage", tagline: "Timeless patterns with heritage charm." },
  { id: "modern-luxe", name: "Modern Luxe", tagline: "Sleek designs for the fashion-forward." },
  { id: "limited-edition", name: "Limited Editions", tagline: "Exclusive drops for collectors." },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickView, setQuickView] = useState(null);
  const [params, setParams] = useSearchParams();
  const active = params.get("c") || "all";

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: active === "all" ? {} : { category: active } })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [active]);

  const activeFilter = useMemo(() => FILTERS.find((f) => f.id === active) || FILTERS[0], [active]);

  const soonestDrop = useMemo(() => {
    const dates = products
      .map((p) => p.drop_ends_at)
      .filter(Boolean)
      .map((d) => new Date(d).getTime())
      .filter((t) => t > Date.now())
      .sort((a, b) => a - b);
    return dates.length ? new Date(dates[0]).toISOString() : null;
  }, [products]);

  return (
    <div className="pt-32" data-testid="shop-page">
      <header className="mx-auto max-w-[1500px] px-6 md:px-10">
        <MaskedLine>
          <p className="mb-4 flex items-center gap-3 text-[0.68rem] font-bold uppercase tracking-[0.34em] text-burgundy">
            <span className="h-px w-10 bg-gold" /> The Collection
          </p>
        </MaskedLine>
        <h1 className="font-serif text-5xl leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-7xl" data-testid="shop-headline">
          <MaskedLine delay={0.1}>Choose your</MaskedLine>
          <MaskedLine delay={0.22}>
            <em className="text-gold">signature step</em>
          </MaskedLine>
        </h1>
        <div className="mt-12 flex flex-wrap gap-2.5" data-testid="shop-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setParams(f.id === "all" ? {} : { c: f.id })}
              data-testid={`filter-${f.id}`}
              className={`border px-6 py-3 text-[0.65rem] font-bold uppercase tracking-[0.24em] transition-colors duration-300 ${
                active === f.id
                  ? "border-navy bg-navy text-cream"
                  : "border-navy/20 text-navy/60 hover:border-navy hover:text-navy"
              }`}
            >
              {f.name}
            </button>
          ))}
        </div>
        <motion.p
          key={active}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-6 font-serif text-xl italic text-navy/60"
          data-testid="shop-category-tagline"
        >
          {activeFilter.tagline}
        </motion.p>
        {active === "limited-edition" && soonestDrop && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-10 flex flex-wrap items-center gap-8 border border-gold/30 bg-gold/5 px-8 py-7"
            data-testid="drop-banner"
          >
            <div>
              <p className="font-serif text-2xl italic text-navy">The vault is open — briefly.</p>
              <p className="mt-1.5 text-[0.62rem] font-bold uppercase tracking-[0.24em] text-navy/50">Numbered pairs. When the clock stops, they're gone.</p>
            </div>
            <DropCountdown until={soonestDrop} variant="block" className="lg:ml-auto" />
          </motion.div>
        )}
      </header>

      <div className="mx-auto max-w-[1500px] px-6 py-16 md:px-10 md:py-24">
        {loading ? (
          <div className="grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3" data-testid="shop-loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-navy/8" />
                <div className="mt-5 h-5 w-2/3 bg-navy/8" />
                <div className="mt-2 h-3 w-1/3 bg-navy/8" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3" data-testid="shop-grid">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} onQuickView={setQuickView} className={i % 3 === 1 ? "lg:mt-16" : ""} />
            ))}
          </div>
        )}
        <Reveal className="mt-24 border-t border-navy/10 pt-10 text-center">
          <p className="font-serif text-2xl italic text-navy/60">One size, tailored stretch — fits EU 36–46 with couture ease.</p>
        </Reveal>
      </div>
      <QuickView product={quickView} onClose={() => setQuickView(null)} />
    </div>
  );
}
